import { useEffect, useRef } from 'react';

interface Props {
  stream: MediaStream | null;
  muted: boolean;
  mirrored?: boolean;
  className?: string;
  onBlockedAutoplay?: (blocked: boolean) => void;
}

/**
 * `<video>` під керуванням ref, поза деревом ререндерів.
 *
 * `playsinline` обовʼязковий: без нього мобільний Safari перехоплює відео у
 * власний повноекранний програвач. `muted` для локального прев'ю — теж не
 * косметика: інакше autoplay-політика просто не запустить відтворення, та й
 * користувач чутиме власний голос.
 */
export function VideoSurface({
  stream,
  muted,
  mirrored = false,
  className,
  onBlockedAutoplay,
}: Props): React.JSX.Element {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (element.srcObject !== stream) {
      element.srcObject = stream;
    }
    if (!stream) return;

    let cancelled = false;
    const attempt = async (): Promise<void> => {
      try {
        await element.play();
        if (!cancelled) onBlockedAutoplay?.(false);
      } catch {
        // Автовідтворення заблоковане: показуємо шлях запустити його дією.
        if (!cancelled) onBlockedAutoplay?.(true);
      }
    };
    void attempt();

    return () => {
      cancelled = true;
    };
  }, [stream, onBlockedAutoplay]);

  return (
    <video
      ref={ref}
      className={className}
      autoPlay
      playsInline
      muted={muted}
      style={mirrored ? { transform: 'scaleX(-1)' } : undefined}
    />
  );
}
